import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

import type { ListEntity, ListMember } from "@/backend/focus";
import {
  addMemberToList,
  getUserAccountListMembers,
  getUserListsRequest,
  removeMembersFromList,
} from "@/backend/focus";
import type { Option } from "@/components/ui/multiselect";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { toast } from "@/components/ui/use-toast";
import { LocalStorageKeys } from "@/utils/local-storage-keys";

export const FOLLOW_LIST_ID = "follow";

export const useUserLists = (
  currentUserPublicKey: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const [userListsLoading, setUserListsLoading] = useState(false);
  const [userListsReady, setUserListsReady] = useState(!currentUserPublicKey);
  const [userLists, setUserLists] = useState<ListEntity[]>([]);
  const [selectedListMembersLoading, setSelectedListMembersLoading] =
    useState(false);
  const resolvedListsPublicKeyRef = useRef<string | null>(null);

  const [selectedListId, setSelectedListId] = useLocalStorage<string | null>(
    LocalStorageKeys.selectedListId,
    FOLLOW_LIST_ID,
  );

  const [selectedListIdMembers, setSelectedListIdMembers] = useState<
    ListMember[]
  >([]);

  const userListOptions = useMemo<Option[]>(() => {
    return [
      {
        value: FOLLOW_LIST_ID,
        label: "Follow",
      },
    ].concat(
      userLists?.map((list: ListEntity) => ({
        value: list.ListId,
        label: list.ListName,
      })) ?? [],
    );
  }, [userLists]);

  const removeListFromLocalState = useCallback((listId: string) => {
    setUserLists((prevLists) => {
      return prevLists.filter((list) => list.ListId !== listId);
    });
  }, []);

  const resetListForLocalState = useCallback((listId: string) => {
    setUserLists((prevLists) => {
      return prevLists.map((list) => {
        if (list.ListId === listId) {
          return {
            ...list,
            MembersCount: 0,
            Members: [],
          };
        }
        return list;
      });
    });
  }, []);

  const updateListForLocalState = useCallback((list: ListEntity) => {
    setUserLists((prevLists) => {
      return prevLists.map((prevList) => {
        if (prevList.ListId === list.ListId) {
          return list;
        }
        return prevList;
      });
    });
  }, []);

  const addListForLocalState = useCallback((list: ListEntity) => {
    setUserLists((prevLists) => {
      return [list, ...prevLists];
    });
  }, []);

  const getUserLists = useCallback(async () => {
    if (!currentUserPublicKey) {
      resolvedListsPublicKeyRef.current = null;
      setUserLists([]);
      setUserListsLoading(false);
      setUserListsReady(true);
      return;
    }

    const isInitialLoadForCurrentUser =
      resolvedListsPublicKeyRef.current !== currentUserPublicKey;

    if (isInitialLoadForCurrentUser) {
      setUserListsReady(false);
    }

    setUserListsLoading(true);
    try {
      const userLists = await getUserListsRequest(currentUserPublicKey);
      const lists = userLists?.Lists || [];

      resolvedListsPublicKeyRef.current = currentUserPublicKey;
      setUserLists(lists);

      if (!lists.some((e) => e.ListId === selectedListId)) {
        setSelectedListId(FOLLOW_LIST_ID);
      }
    } finally {
      setUserListsLoading(false);
      setUserListsReady(true);
    }
  }, [currentUserPublicKey, selectedListId, setSelectedListId]);

  useEffect(() => {
    if (!currentUserPublicKey) {
      resolvedListsPublicKeyRef.current = null;
      setUserLists([]);
      setSelectedListIdMembers([]);
      setUserListsLoading(false);
      setUserListsReady(true);
      return;
    }

    if (!enabled) {
      if (resolvedListsPublicKeyRef.current !== currentUserPublicKey) {
        setUserLists([]);
        setSelectedListIdMembers([]);
      }
      setUserListsLoading(false);
      setUserListsReady(false);
      return;
    }

    getUserLists();
  }, [currentUserPublicKey, enabled, getUserLists]);

  useEffect(() => {
    const r = async () => {
      if (
        !enabled ||
        !selectedListId ||
        !currentUserPublicKey ||
        selectedListId === FOLLOW_LIST_ID
      ) {
        if (selectedListId === FOLLOW_LIST_ID || !enabled) {
          setSelectedListIdMembers([]);
        }
        return;
      }

      setSelectedListMembersLoading(true);

      try {
        const data = await getUserAccountListMembers({
          listId: selectedListId,
          publicKey: currentUserPublicKey,
          limit: 1000,
          offset: 0,
        });

        setSelectedListIdMembers(data?.Members || []);
      } finally {
        setSelectedListMembersLoading(false);
      }
    };
    r();
  }, [currentUserPublicKey, enabled, selectedListId]);

  const addMemberToListFlow = useCallback(
    async ({ listId, userIds }: { listId: string; userIds: string[] }) => {
      try {
        await addMemberToList({
          listId,
          userPublicKeyBase58Check: currentUserPublicKey,
          userPublicKeysBase58Check: userIds,
        });

        if (listId === selectedListId) {
          setSelectedListIdMembers((members) => {
            return [
              ...members,
              ...userIds.map((userId) => {
                return {
                  ListMemberPublicKeyBase58Check: userId,
                  ListId: listId,
                  List: null,
                  CreatedAt: new Date().toISOString(),
                } as ListMember;
              }, []),
            ];
          });
        }

        toast({
          variant: "success",
          title: "Success",
          description: "The list has been updated successfully!",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when adding user to list`,
          details: createToastErrorDetails(error),
        });
      }
    },
    [currentUserPublicKey, selectedListId],
  );

  const removeMembersFromListFlow = useCallback(
    async ({ listId, memberIds }: { listId: string; memberIds: string[] }) => {
      try {
        await removeMembersFromList({
          listId,
          userPublicKeyBase58Check: currentUserPublicKey,
          userPublicKeysBase58Check: memberIds,
        });

        if (listId === selectedListId) {
          setSelectedListIdMembers((members) => {
            return members.filter(
              (member) =>
                !memberIds.includes(member.ListMemberPublicKeyBase58Check),
            );
          });
        }
        toast({
          variant: "success",
          title: "Success",
          description: "The list has been updated successfully!",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when removing users from list`,
          details: createToastErrorDetails(error),
        });
      }
    },
    [currentUserPublicKey, selectedListId],
  );

  const selectedList =
    userLists.find((list) => list.ListId === selectedListId) ?? null;

  return {
    userListsLoading,
    userListsReady,
    getUserLists,
    userListOptions,
    selectedListMembersLoading,
    selectedListIdMembers,
    addMemberToListFlow,
    userLists,
    removeListFromLocalState,
    resetListForLocalState,
    updateListForLocalState,
    addListForLocalState,
    removeMembersFromListFlow,
    selectedListId,
    selectedList,
    setSelectedListId,
  };
};
